import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, MapPin, AlertCircle } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({ click(e) { onLocationSelect(e.latlng.lat, e.latlng.lng); } });
  return null;
};

const FlyTo = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom || 15, { duration: 1 });
  }, [position, zoom, map]);
  return null;
};

export const LocationPickerModal = ({
  open, onClose, onConfirm,
  initialLat, initialLng,
  initialCommuneId, initialAddress,
  communes = [],
}) => {
  // Coordinates default to Santiago only when the modal opens without any
  // initial coords. When it opens with coords we jump directly to them so the
  // map never briefly renders at the wrong location.
  const hasInitialCoords = Number.isFinite(initialLat) && Number.isFinite(initialLng);
  const [lat, setLat] = useState(hasInitialCoords ? initialLat : -33.4489);
  const [lng, setLng] = useState(hasInitialCoords ? initialLng : -70.6693);
  const [selectedCommuneId, setSelectedCommuneId] = useState(initialCommuneId || '');
  const [addressQuery, setAddressQuery] = useState(initialAddress || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(15);
  const [communeBBox, setCommuneBBox] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Bumped every time we open the modal so <MapContainer> remounts with the
  // freshly computed `center`, avoiding stale centering from a previous open.
  const [mapKey, setMapKey] = useState(0);
  const debounceRef = useRef(null);
  const suggestionsRef = useRef(null);
  // Captures the initial coords/commune at open time so the async commune
  // geocoder can't overwrite user data by reading stale closure props.
  const initialCoordsRef = useRef({ hasCoords: false, communeId: '' });
  // True only when the user manually switches the commune dropdown after
  // opening — that's the only case we want to re-center on commune.
  const userChangedCommuneRef = useRef(false);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset on open — snapshot initial props into refs so the async commune
  // geocoder decides based on the value at open time, not on whatever the
  // parent passes on later re-renders.
  useEffect(() => {
    if (!open) return;
    const hasCoords = Number.isFinite(initialLat) && Number.isFinite(initialLng);
    initialCoordsRef.current = {
      hasCoords,
      communeId: initialCommuneId || '',
    };
    userChangedCommuneRef.current = false;
    setNotFound(false);
    setSuggestions([]);
    setShowSuggestions(false);
    setCommuneBBox(null);
    setSelectedCommuneId(initialCommuneId || '');
    setAddressQuery(initialAddress || '');
    if (hasCoords) {
      setLat(initialLat);
      setLng(initialLng);
      setFlyTarget([initialLat, initialLng]);
      setFlyZoom(15);
    } else {
      // Fall back to Santiago until the commune geocoder resolves a better
      // center below.
      setLat(-33.4489);
      setLng(-70.6693);
      setFlyTarget(null);
    }
    // Force MapContainer to remount so its initial `center` reflects the
    // venue coords instead of leftover state from a previous open.
    setMapKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When commune changes → geocode commune to center map + get bbox for the
  // address autocomplete. We only recenter/fly when either (a) the venue has
  // no saved coords, or (b) the user manually changed the commune dropdown
  // after opening the modal.
  useEffect(() => {
    if (!open || !selectedCommuneId) return;
    const commune = communes.find(c => c.id === selectedCommuneId);
    if (!commune) return;

    let cancelled = false;
    const geocodeCommune = async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(commune.name + ', Chile')}&limit=1&countrycodes=cl`
        );
        const data = await res.json();
        if (cancelled) return;
        if (data?.length > 0) {
          const cLat = parseFloat(data[0].lat);
          const cLng = parseFloat(data[0].lon);
          const bbox = data[0].boundingbox;
          setCommuneBBox(bbox);
          // Read the ref (not props) so a re-render mid-fetch can't flip the
          // decision. Only recenter when we have no initial coords or when
          // the user actively switched the commune.
          const shouldRecenter =
            userChangedCommuneRef.current || !initialCoordsRef.current.hasCoords;
          if (shouldRecenter) {
            setLat(cLat);
            setLng(cLng);
            setFlyTarget([cLat, cLng]);
            setFlyZoom(13);
          }
        }
      } catch (err) {
        console.error('Commune geocode error:', err);
      }
    };
    geocodeCommune();
    return () => { cancelled = true; };
  }, [open, selectedCommuneId, communes]);

  // Debounced autocomplete as user types address
  const handleAddressChange = useCallback((value) => {
    setAddressQuery(value);
    setNotFound(false);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Coordinate shortcut: if the input looks like "lat, lng" (with any
    // number of spaces / comma / semicolon separator, optional signs, up to
    // ~10 decimals), skip Nominatim entirely — jump the map to that point
    // and reverse-geocode to fill the address text.
    const coordMatch = value.trim().match(
      /^(-?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/
    );
    if (coordMatch) {
      const parsedLat = parseFloat(coordMatch[1]);
      const parsedLng = parseFloat(coordMatch[2]);
      if (
        Number.isFinite(parsedLat) &&
        Number.isFinite(parsedLng) &&
        parsedLat >= -90 && parsedLat <= 90 &&
        parsedLng >= -180 && parsedLng <= 180
      ) {
        setSuggestions([]);
        setShowSuggestions(false);
        setNotFound(false);
        setLat(parsedLat);
        setLng(parsedLng);
        setFlyTarget([parsedLat, parsedLng]);
        setFlyZoom(17);
        // Reverse-geocode to display a nice address (non-blocking).
        (async () => {
          try {
            const rr = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${parsedLat}&lon=${parsedLng}&zoom=18&addressdetails=1&countrycodes=cl`
            );
            if (!rr.ok) return;
            const dd = await rr.json();
            if (dd && dd.address) {
              const a = dd.address;
              const road = a.road || a.pedestrian || a.footway || a.residential || '';
              const num = a.house_number || '';
              const nice = [road, num].filter(Boolean).join(' ').trim();
              if (nice) setAddressQuery(nice);
            }
          } catch (_) { /* silent */ }
        })();
        return;
      }
    }

    debounceRef.current = setTimeout(async () => {
      const commune = communes.find(c => c.id === selectedCommuneId);
      const communeName = commune?.name || '';

      setLoadingSuggestions(true);
      try {
        // Two-pass geocoding strategy for better Chilean street coverage:
        //   Pass 1: Structured query (street=..., city=..., country=Chile)
        //           biased by commune viewbox but NOT bounded — biases without
        //           excluding matches Nominatim only finds slightly outside
        //           the commune polygon.
        //   Pass 2: Free-form fallback ("<term>, <commune>, Chile") biased
        //           the same way. Catches POIs / venues that don't parse as
        //           a street.
        // Merge, dedupe by display_name, keep the top 6.
        const vb = communeBBox
          ? `&viewbox=${communeBBox[2]},${communeBBox[0]},${communeBBox[3]},${communeBBox[1]}`
          : '';

        const urlStructured =
          `https://nominatim.openstreetmap.org/search?format=json&limit=6&countrycodes=cl&addressdetails=1` +
          `&street=${encodeURIComponent(value)}` +
          (communeName ? `&city=${encodeURIComponent(communeName)}` : '') +
          `&country=Chile${vb}`;

        const urlFree =
          `https://nominatim.openstreetmap.org/search?format=json&limit=6&countrycodes=cl&addressdetails=1` +
          `&q=${encodeURIComponent(`${value}, ${communeName}, Chile`)}${vb}`;

        const [r1, r2] = await Promise.all([
          fetch(urlStructured).then(r => r.ok ? r.json() : []).catch(() => []),
          fetch(urlFree).then(r => r.ok ? r.json() : []).catch(() => []),
        ]);

        const seen = new Set();
        const merged = [];
        for (const d of [...(r1 || []), ...(r2 || [])]) {
          const key = d.display_name;
          if (!key || seen.has(key)) continue;
          seen.add(key);
          merged.push({
            display: d.display_name.split(',').slice(0, 3).join(', '),
            lat: parseFloat(d.lat),
            lng: parseFloat(d.lon),
          });
          if (merged.length >= 6) break;
        }

        if (merged.length > 0) {
          setSuggestions(merged);
          setShowSuggestions(true);
          setNotFound(false);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
          setNotFound(true);
        }
      } catch (err) {
        console.error('Autocomplete error:', err);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 400);
  }, [selectedCommuneId, communes, communeBBox]);

  const selectSuggestion = (s) => {
    setLat(s.lat);
    setLng(s.lng);
    setFlyTarget([s.lat, s.lng]);
    setFlyZoom(17);
    setAddressQuery(s.display);
    setSuggestions([]);
    setShowSuggestions(false);
    setNotFound(false);
  };

  const handleLocationSelect = (newLat, newLng) => {
    setLat(newLat);
    setLng(newLng);
    setNotFound(false);
    // Reverse-geocode the pin drop so the "Dirección" field on the parent
    // form auto-fills with the closest street + house number (e.g. "Avenida
    // República de Croacia 738"). Non-blocking: if the request fails, the
    // admin can still type the address manually.
    (async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLat}&lon=${newLng}&zoom=18&addressdetails=1&countrycodes=cl`
        );
        if (!res.ok) return;
        const d = await res.json();
        if (d && d.address) {
          const a = d.address;
          const road = a.road || a.pedestrian || a.footway || a.residential || '';
          const num = a.house_number || '';
          const nice = [road, num].filter(Boolean).join(' ').trim();
          if (nice) setAddressQuery(nice);
          else if (d.display_name) {
            // Fallback: first 3 segments of the free-form display name.
            setAddressQuery(d.display_name.split(',').slice(0, 2).join(', ').trim());
          }
        }
      } catch (_) {
        // silent
      }
    })();
  };

  const handleConfirm = () => {
    // Send the resolved address string back so the parent can auto-fill its
    // "Dirección" input. Trim so we don't overwrite manual edits with just
    // whitespace.
    onConfirm(lat, lng, (addressQuery || '').trim() || null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-4 pb-3">
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#064E3B]" />
            Seleccionar ubicación del evento
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 space-y-3 pb-3">
          {/* Commune selector */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Comuna</Label>
            <Select value={selectedCommuneId} onValueChange={(v) => { userChangedCommuneRef.current = true; setSelectedCommuneId(v); setSuggestions([]); setNotFound(false); }}>
              <SelectTrigger data-testid="map-commune-select">
                <SelectValue placeholder="Seleccionar comuna" />
              </SelectTrigger>
              <SelectContent>
                {communes.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Address with autocomplete */}
          <div className="space-y-1 relative" ref={suggestionsRef}>
            <Label className="text-xs text-gray-500">Escribe el nombre de la calle y te acercamos para que marques con un click el lugar del evento</Label>
            <div className="relative">
              <Input
                placeholder="Ej: Coquimbo, Av. Grecia, Plaza de Armas..."
                value={addressQuery}
                onChange={(e) => handleAddressChange(e.target.value)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                data-testid="map-address-input"
              />
              {loadingSuggestions && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              )}
            </div>

            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto" data-testid="address-suggestions">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 transition-colors border-b border-gray-50 last:border-0 flex items-start gap-2"
                    onClick={() => selectSuggestion(s)}
                    data-testid={`suggestion-${i}`}
                  >
                    <MapPin className="w-3.5 h-3.5 text-[#064E3B] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{s.display}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Not found message */}
          {notFound && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="address-not-found">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">
                No encontramos la dirección, pero no te preocupes! Dirígete al lugar del evento en el mapa y haz click sobre el lugar.
              </p>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="h-[340px] w-full" data-testid="location-picker-map">
          <MapContainer
            key={mapKey}
            center={[lat, lng]}
            zoom={hasInitialCoords || flyTarget ? 15 : 13}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[lat, lng]} />
            <MapClickHandler onLocationSelect={handleLocationSelect} />
            {flyTarget && <FlyTo position={flyTarget} zoom={flyZoom} />}
          </MapContainer>
        </div>

        {/* Coordinates */}
        <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600 flex items-center justify-between">
          <span>
            Lat: <strong>{lat.toFixed(6)}</strong> · Lng: <strong>{lng.toFixed(6)}</strong>
          </span>
          <span className="text-xs text-gray-400">Haz click en el mapa para ajustar</span>
        </div>

        <DialogFooter className="p-4 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            className="bg-[#064E3B] hover:bg-[#053D2E] text-white"
            onClick={handleConfirm}
            data-testid="confirm-location-btn"
          >
            <MapPin className="w-4 h-4 mr-1" />
            Confirmar ubicación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
